import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { isSyllySyncDomain, isEduDomain, getEmailDomain, getOrganization, isOrgAdmin, isOrgTeamMember, hasApprovedMemberRequest, getAccessibleOrganizations } from '../services/authService';

export const SELECTED_ORG_STORAGE_KEY = 'syllysync_selected_org';
export const PENDING_BILLING_MODAL_STORAGE_KEY = 'syllysync_pending_billing_modal_org';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null); // 'syllysync' or 'edu'
  const [isOrgAdminUser, setIsOrgAdminUser] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [isOrgMember, setIsOrgMember] = useState(false); // Approved member (not admin)
  const [orgDomain, setOrgDomainState] = useState(null);
  const [accessibleOrgs, setAccessibleOrgs] = useState([]);

  const setOrgDomain = useCallback((orgId) => {
    if (orgId != null) {
      try {
        localStorage.setItem(SELECTED_ORG_STORAGE_KEY, orgId);
      } catch (e) {
        // ignore
      }
    }
    setOrgDomainState(orgId);
  }, []);

  const switchOrganization = useCallback(async (orgId) => {
    if (currentUser && currentUser.email && orgId) {
      try {
        localStorage.setItem(SELECTED_ORG_STORAGE_KEY, orgId);
      } catch (e) {
        // ignore
      }
      setOrgDomainState(orgId);
      await refreshOrgAdminStatus(currentUser, currentUser.email, orgId);
    }
  }, [currentUser]);

  const refreshOrgAdminStatus = async (user, email, selectedOrgId) => {
    if (!user || !email) return;

    if (isSyllySyncDomain(email)) {
      setOrgDomainState(null);
      setAccessibleOrgs([]);
      setIsOrgAdminUser(false);
      setIsTeamMember(false);
      setIsOrgMember(false);
      return;
    }

    const orgs = await getAccessibleOrganizations(user.uid);
    setAccessibleOrgs(orgs);

    if (orgs.length === 0) {
      setOrgDomainState(null);
      setIsOrgAdminUser(false);
      setIsTeamMember(false);
      setIsOrgMember(false);
      return;
    }

    const orgId = selectedOrgId != null && orgs.some((o) => o.id === selectedOrgId)
      ? selectedOrgId
      : (() => {
          try {
            const stored = localStorage.getItem(SELECTED_ORG_STORAGE_KEY);
            if (stored && orgs.some((o) => o.id === stored)) return stored;
          } catch (e) {
            // ignore
          }
          return orgs[0].id;
        })();

    setOrgDomainState(orgId);
    const org = await getOrganization(orgId);
    if (org) {
      const adminStatus = await isOrgAdmin(user.uid, orgId);
      setIsOrgAdminUser(adminStatus);
      if (!adminStatus) {
        const teamMemberStatus = await isOrgTeamMember(user.uid, orgId);
        setIsTeamMember(teamMemberStatus);
        if (!teamMemberStatus) {
          const approvedMemberStatus = await hasApprovedMemberRequest(user.uid, orgId);
          setIsOrgMember(approvedMemberStatus);
        } else {
          setIsOrgMember(false);
        }
      } else {
        setIsTeamMember(false);
        setIsOrgMember(false);
      }
    } else {
      setIsOrgAdminUser(false);
      setIsTeamMember(false);
      setIsOrgMember(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user && user.email) {
        if (isSyllySyncDomain(user.email)) {
          setUserType('syllysync');
          setIsOrgAdminUser(false);
          setIsTeamMember(false);
          setOrgDomainState(null);
          setAccessibleOrgs([]);
        } else if (isEduDomain(user.email)) {
          setUserType('edu');
          await refreshOrgAdminStatus(user, user.email, null);
        } else {
          setUserType(null);
          setIsOrgAdminUser(false);
          setIsTeamMember(false);
          setIsOrgMember(false);
          setOrgDomainState(null);
          setAccessibleOrgs([]);
        }
      } else {
        setUserType(null);
        setIsOrgAdminUser(false);
        setIsTeamMember(false);
        setIsOrgMember(false);
        setOrgDomainState(null);
        setAccessibleOrgs([]);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshAuth = async () => {
    if (currentUser && currentUser.email) {
      await refreshOrgAdminStatus(currentUser, currentUser.email, orgDomain);
    }
  };

  const value = {
    currentUser,
    userType,
    loading,
    isAuthorized: userType !== null,
    isSyllySyncAdmin: userType === 'syllysync',
    isEduAdmin: userType === 'edu',
    isOrgAdminUser,
    isTeamMember,
    isOrgMember,
    orgDomain,
    accessibleOrgs,
    setOrgDomain,
    switchOrganization,
    refreshAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
